<template>
  <div class="min-h-screen relative mt-[75px]">
    <div
      class="relative bg-white/80 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50 shadow-xl shadow-gray-200/20"
    >
      <div class="max-w-7xl mx-auto px-3 md:px-6 py-3 md:py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3 md:space-x-6 flex-1 min-w-0">
            <button
              @click="confirmExit"
              class="group cursor-pointer relative overflow-hidden bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white p-2 md:p-2.5 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl flex-shrink-0"
            >
              <span class="relative z-10">
                <i class="fa-solid fa-arrow-left text-sm md:text-lg"></i>
              </span>
              <div
                class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
              ></div>
            </button>

            <div class="space-y-1 min-w-0 flex-1">
              <h1
                class="text-base md:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent truncate"
              >
                {{ currentExercice?.title }}
              </h1>
              <div
                v-if="['LISTENING', 'READING'].includes(ExerciceType)"
                class="flex items-center space-x-2 md:space-x-3 text-xs md:text-sm text-gray-600"
              >
                <div class="flex items-center space-x-1">
                  <i class="fa-solid fa-list-ol text-primary-500"></i>
                  <span class="font-medium"
                    >Question {{ currentQuestionIndex + 1 }} sur {{ totalQuestions }}</span
                  >
                </div>
              </div>
              <div v-else class="text-xs md:text-sm text-gray-600 line-clamp-2">
                {{ currentExercice?.instructions }}
              </div>
            </div>
          </div>

          <div v-if="hasTimeLimit" class="flex items-center space-x-2 md:space-x-4 flex-shrink-0">
            <div class="relative">
              <div class="relative flex items-center space-x-2 md:space-x-3 rounded-2xl">
                <div
                  class="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center"
                  :class="{
                    'bg-gradient-to-br from-red-500 to-red-600': timeRemaining <= 2 * 60,
                    'bg-gradient-to-br from-yellow-500 to-orange-600':
                      timeRemaining <= 5 * 60 && timeRemaining > 2 * 60,
                    'bg-gradient-to-br from-green-500 to-emerald-600': timeRemaining > 5 * 60,
                  }"
                >
                  <i class="fa-solid fa-clock text-white text-xs md:text-sm"></i>
                </div>
                <div class="text-center">
                  <div
                    class="font-mono font-bold text-lg md:text-2xl"
                    :class="{
                      'text-red-700': timeRemaining <= 2 * 60,
                      'text-yellow-700': timeRemaining <= 5 * 60 && timeRemaining > 2 * 60,
                      'text-green-700': timeRemaining > 5 * 60,
                    }"
                  >
                    {{ formatTime(timeRemaining) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Enhanced Progress Bar -->
      <div class="absolute bottom-0 left-0 w-full h-1 bg-gray-200/50">
        <div
          class="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out relative overflow-hidden"
          :style="{ width: `${(currentQuestionIndex / totalQuestions) * 100}%` }"
        >
          <div class="absolute inset-0 bg-white/30 animate-pulse"></div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <LoadingState v-if="isLoading"></LoadingState>

    <!-- Enhanced Error state -->
    <div v-else-if="error" class="relative max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-16">
      <div
        class="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200/50 rounded-2xl md:rounded-3xl p-6 md:p-12 text-center shadow-xl shadow-red-100/50 backdrop-blur-sm"
      >
        <div
          class="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-lg"
        >
          <i class="fa-solid fa-exclamation-triangle text-white text-xl md:text-2xl"></i>
        </div>
        <h3 class="text-xl md:text-2xl font-bold text-red-900 mb-2 md:mb-3">
          Erreur de chargement
        </h3>
        <p class="text-red-700 mb-6 md:mb-8 text-base md:text-lg">{{ error }}</p>
        <button
          @click="retry"
          class="relative overflow-hidden group bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3 px-6 md:py-4 md:px-8 rounded-xl md:rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <span class="relative z-10 flex items-center">
            <i class="fa-solid fa-refresh mr-2"></i>
            Réessayer
          </span>
          <div
            class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
          ></div>
        </button>
      </div>
    </div>

    <div v-else class="relative max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
      <div>
        <div
          class="bg-white/80 backdrop-blur-xl border border-white/20 rounded-xl md:rounded-2xl p-3 md:p-4 mb-6 md:mb-8 shadow-xl shadow-gray-200/50"
        >
          <div class="flex flex-wrap gap-2 mb-3">
            <button
              v-for="(question, index) in questions"
              :key="question.slug"
              @click="goToQuestion(index)"
              class="group relative w-8 h-8 md:w-10 md:h-10 cursor-pointer rounded-lg font-bold text-xs md:text-sm transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
              :class="{
                'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-primary-500/25':
                  currentQuestion?.slug === question?.slug,
                'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-green-500/25':
                  selectedChoices[question?.slug] != null &&
                  currentQuestion?.slug !== question?.slug,
                'bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 hover:from-gray-300 hover:to-gray-400':
                  selectedChoices[question?.slug] == null &&
                  currentQuestion?.slug !== question?.slug,
              }"
            >
              <span class="relative z-10">{{ index + 1 }}</span>
              <div
                v-if="selectedChoices[question?.slug] != null"
                class="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3 h-3 md:w-4 md:h-4 bg-white rounded-full flex items-center justify-center"
              >
                <i class="fa-solid fa-check text-green-500 text-xs"></i>
              </div>
            </button>
          </div>

          <!-- Legend -->
          <div class="flex flex-wrap gap-4 md:gap-6 text-sm">
            <div class="flex items-center space-x-2">
              <div class="w-2 h-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg"></div>
              <span class="font-medium text-xs md:text-sm text-gray-700">Répondue</span>
            </div>
            <div class="flex items-center space-x-2">
              <div class="w-2 h-2 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
              <span class="font-medium text-xs md:text-sm text-gray-700">Non répondue</span>
            </div>
            <div class="flex items-center space-x-2">
              <div
                class="w-2 h-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg"
              ></div>
              <span class="font-medium text-xs md:text-sm text-gray-700">Question actuelle</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="['LISTENING', 'READING'].includes(ExerciceType)">
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-8">
          <div class="space-y-4 md:space-y-6">
            <div class="">
              <div class="space-y-4 md:space-y-6">
                <div
                  v-for="(ressource, index) in currentQuestion.ressouces"
                  :key="index"
                  class="group"
                >
                  <div
                    v-if="ressource.image_file"
                    class="relative border-2 border-gray-200 rounded-lg"
                  >
                    <img
                      :src="ressource.image_file"
                      alt="Ressource visuelle"
                      class="w-full h-auto duration-300"
                    />
                  </div>

                  <div v-if="ressource.audio_file" class="mt-4">
                    <AudioPlayer
                      :src="ressource.audio_file"
                      :practiceType="practiceType"
                      class="rounded-xl md:rounded-2xl overflow-hidden shadow-lg"
                    />
                  </div>

                  <div
                    v-if="ressource.audio_file"
                    class="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl md:rounded-2xl p-3 md:p-4"
                  >
                    <div class="flex items-start space-x-3">
                      <div
                        class="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      >
                        <i class="fa-solid fa-info-circle text-white text-xs md:text-sm"></i>
                      </div>
                      <div>
                        <p class="text-amber-800 text-sm md:text-base">{{ ressource.title }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-4 md:space-y-6">
            <div class="flex justify-end">
              <span class="pt-4">{{ currentQuestion?.points }} points</span>
            </div>
            <div
              v-if="currentQuestion"
              class="bg-white rounded-xl border-2 border-gray-200 px-4 md:px-5 pb-3"
            >
              <div class="flex items-center justify-end pt-2 mb-6 md:mb-8">
                <div v-if="isSubmittingAnswer" class="flex items-center space-x-2 text-primary-600">
                  <i class="fa-solid fa-spinner fa-spin text-base md:text-lg"></i>
                </div>
              </div>

              <div
                v-if="['MCQ'].includes(currentQuestion.question_type)"
                class="space-y-3 md:space-y-4 mb-6 md:mb-8"
              >
                <div
                  v-for="(choice, index) in currentQuestion.choices"
                  :key="choice.slug"
                  @click="selectChoice(choice)"
                  class="group relative overflow-hidden cursor-pointer transition-all duration-300 transform rounded-xl md:rounded-2xl"
                  :class="getChoiceClass(choice)"
                >
                  <div
                    class="flex items-start space-x-3 md:space-x-4 px-4 md:px-6 py-3 rounded-xl md:rounded-2xl border-2 transition-all duration-300"
                    :class="
                      isChoiceSelected(choice)
                        ? 'border-primary-500 bg-gradient-to-r from-primary-50 to-blue-50'
                        : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/30'
                    "
                  >
                    <div
                      class="flex items-center justify-center w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all duration-300 mt-1"
                      :class="
                        isChoiceSelected(choice)
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300 group-hover:border-primary-400'
                      "
                    >
                      <div
                        class="w-3 h-3 rounded-full transition-all duration-300"
                        :class="isChoiceSelected(choice) ? 'bg-white' : 'bg-transparent'"
                      ></div>
                    </div>

                    <div class="flex-1 min-w-0">
                      <p
                        class="text-gray-800 font-medium text-sm md:text-lg leading-relaxed transition-colors duration-300"
                        :class="
                          isChoiceSelected(choice)
                            ? 'text-primary-900'
                            : 'group-hover:text-primary-800'
                        "
                      >
                        {{ choice.choice_text }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="flex items-center justify-between pt-6 md:pt-8 border-t-2 border-gray-200/50 gap-3"
              >
                <button
                  @click="previousQuestion"
                  :disabled="currentQuestionIndex === 0"
                  class="group relative cursor-pointer overflow-hidden flex items-center space-x-2 md:space-x-3 px-4 md:px-6 py-3 md:py-4 text-gray-700 border-2 border-gray-200 rounded-xl md:rounded-2xl font-semibold transition-all duration-300 hover:border-primary-300 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 text-sm md:text-base"
                >
                  <span class="relative z-10 flex items-center space-x-1 md:space-x-2">
                    <i class="fa-solid fa-arrow-left text-xs md:text-sm"></i>
                    <span class="hidden sm:inline">Précédent</span>
                  </span>
                  <div
                    class="absolute inset-0 bg-gradient-to-r from-primary-50 to-blue-50 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
                  ></div>
                </button>

                <div class="text-center">
                  <div
                    class="bg-gradient-to-r from-gray-100 to-gray-200 px-3 md:px-4 py-2 rounded-xl"
                  >
                    <span class="text-xs md:text-sm font-bold text-gray-700">
                      {{ currentQuestionIndex + 1 }} / {{ totalQuestions }}
                    </span>
                  </div>
                </div>

                <button
                  @click="
                    currentQuestionIndex === totalQuestions - 1 ? confirmFinish() : nextQuestion()
                  "
                  :disabled="isSubmittingAnswer"
                  class="group relative cursor-pointer overflow-hidden flex items-center space-x-2 md:space-x-3 px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl md:rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm md:text-base"
                >
                  <span class="relative z-10 flex items-center space-x-1 md:space-x-2">
                    <span class="hidden sm:inline">{{
                      currentQuestionIndex === totalQuestions - 1 ? 'Terminer' : 'Suivant'
                    }}</span>
                    <span class="sm:hidden">{{
                      currentQuestionIndex === totalQuestions - 1 ? 'Fin' : 'Suiv.'
                    }}</span>
                    <i
                      :class="
                        currentQuestionIndex === totalQuestions - 1
                          ? 'fa-solid fa-flag-checkered'
                          : 'fa-solid fa-arrow-right'
                      "
                      class="text-xs md:text-sm"
                    ></i>
                  </span>
                  <div
                    class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-right duration-300"
                  ></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="bg-white/80 backdrop-blur-xl border border-white/20 rounded-xl md:rounded-2xl p-3 md:p-4 flex flex-col gap-3 mb-6 md:mb-8 shadow-xl shadow-gray-200/50"
      >
        <div
          class="p-4 md:p-5 border-primary-200 border-2 rounded-xl bg-primary-50/50 flex items-start flex flex-col gap-3"
        >
          <span class="px-3 md:px-5 py-1 rounded-lg bg-primary-400 text-white text-sm md:text-base"
            >Tâche {{ currentQuestion?.order }}</span
          >
          <div class="text-sm md:text-base">
            {{ getQuestionTitle(currentQuestion?.question_text) }}
          </div>
          <div v-if="currentQuestion?.order === 3">
            <div
              class="mb-4 md:mb-5"
              v-for="(document, idx) in get_parsed(currentQuestion?.question_text)?.documents"
            >
              <div>
                <div class="px-3 md:px-5 py-1 rounded-lg bg-primary-100 mb-2 text-sm md:text-base">
                  Document {{ idx + 1 }}
                </div>
              </div>
              <div class="text-sm md:text-base">{{ document }}</div>
            </div>
          </div>
        </div>

        <div class="" v-if="['WRITING'].includes(ExerciceType)">
          <textarea
            v-model="writingResponses[currentQuestionIndex]"
            rows="8"
            class="w-full border border-2 border-gray-200 bg-gray-50 p-4 md:p-5 rounded-xl text-sm md:text-base"
            placeholder="Écrivez ici..."
          ></textarea>
        </div>

        <div class="" v-else>
          <!-- Composant d'enregistrement vocal -->
          <VoiceRecorder
            ref="voiceRecorderRef"
            :question-slug="currentQuestion.slug"
            @key="currentQuestion.slug"
            :max-duration="180"
            @recording-complete="onRecordingComplete"
            class="mb-4 md:mb-6"
          />
        </div>

        <div class="flex justify-end">
          <button
            @click="saveResponse(currentQuestionIndex)"
            :disabled="isSubmittingAnswer"
            class="group relative cursor-pointer overflow-hidden flex items-center space-x-2 md:space-x-3 px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl md:rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm md:text-base"
          >
            <span class="relative z-10 flex items-center space-x-1 md:space-x-2">
              <span v-if="isSubmittingAnswer">
                <i class="fa-solid fa-spinner fa-spin mr-1"></i>
                <span class="hidden sm:inline">Veuillez patienter</span>
                <span class="sm:hidden">Envoi...</span>
              </span>
              <span v-else>
                <span class="hidden sm:inline">{{
                  currentQuestionIndex === totalQuestions - 1 ? 'Terminer' : 'Suivant'
                }}</span>
                <span class="sm:hidden">{{
                  currentQuestionIndex === totalQuestions - 1 ? 'Fin' : 'Suiv.'
                }}</span>
              </span>
              <i
                :class="
                  currentQuestionIndex === totalQuestions - 1
                    ? 'fa-solid fa-flag-checkered'
                    : 'fa-solid fa-arrow-right'
                "
                class="text-xs md:text-sm"
              ></i>
            </span>
            <div
              class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-right duration-300"
            ></div>
          </button>
        </div>
      </div>
    </div>

    <!-- Exit Modal -->
    <div
      v-if="showExitModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div
        class="bg-white/95 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl border border-white/20"
      >
        <div class="text-center">
          <div
            class="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6"
          >
            <i class="fa-solid fa-exclamation-triangle text-white text-xl md:text-2xl"></i>
          </div>
          <h3 class="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
            Quitter l'exercice ?
          </h3>
          <p class="text-gray-600 mb-6 md:mb-8 text-base md:text-lg">
            Êtes-vous sûr de vouloir quitter cet exercice ? Votre progression actuelle sera perdue.
          </p>

          <div class="flex gap-3 md:gap-4">
            <button
              @click="showExitModal = false"
              class="flex-1 relative cursor-pointer overflow-hidden group px-4 md:px-6 py-3 md:py-4 text-gray-700 border-2 border-gray-200 rounded-xl md:rounded-2xl font-semibold transition-all duration-300 hover:border-gray-300 hover:bg-gray-50 text-sm md:text-base"
            >
              <span class="relative z-10">Annuler</span>
            </button>
            <button
              @click="exitExercice"
              class="flex-1 relative cursor-pointer overflow-hidden group bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 text-sm md:text-base"
            >
              <span class="relative z-10">Quitter</span>
              <div
                class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-center duration-300"
              ></div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Finish Modal -->
    <div
      v-if="showFinishModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div
        class="bg-white/95 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl border border-white/20"
      >
        <div class="text-center">
          <div
            class="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6"
          >
            <i class="fa-solid fa-flag-checkered text-white text-xl md:text-2xl"></i>
          </div>
          <h3 class="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
            Terminer l'exercice ?
          </h3>
          <div
            class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl md:rounded-2xl p-4 md:p-6 mb-4 md:mb-6"
          >
            <p class="text-gray-700 text-base md:text-lg">
              Vous avez répondu à
              <strong class="text-primary-600">{{
                ['LISTENING', 'READING'].includes(ExerciceType)
                  ? Object.keys(selectedChoices).length
                  : ['WRITING'].includes(ExerciceType)
                    ? writingResponses?.length
                    : recorded_audios?.length
              }}</strong>
              question(s) sur <strong class="text-primary-600">{{ totalQuestions }}</strong
              >.
            </p>
          </div>
          <p class="text-gray-600 mb-6 md:mb-8 text-sm md:text-base">
            Voulez-vous vraiment terminer l'exercice maintenant ?
          </p>

          <div class="flex gap-3 md:gap-4">
            <button
              @click="showFinishModal = false"
              class="flex-1 relative overflow-hidden group px-4 md:px-6 py-3 md:py-4 text-gray-700 border-2 border-gray-200 rounded-xl md:rounded-2xl font-semibold transition-all duration-300 hover:border-gray-300 hover:bg-gray-50 text-sm md:text-base"
            >
              <span class="relative z-10">Continuer</span>
            </button>
            <button
              @click="finishExercice"
              class="flex-1 relative overflow-hidden group bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 text-sm md:text-base"
            >
              <span class="relative z-10 flex items-center justify-center space-x-2">
                <i class="fa-solid fa-check"></i>
                <span>Terminer</span>
              </span>
              <div
                class="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-center duration-300"
              ></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Barre de progression mobile -->
  <div
    v-if="hasTimeLimit"
    class="fixed bottom-0 bg-white py-3 md:py-4 px-2 left-0 w-full z-50"
    style="box-shadow: 1px 1px 9px 4px #ececec"
  >
    <div class="bg-gray-200 h-1.5 rounded-full overflow-hidden">
      <div
        class="h-full rounded-full transition-all duration-500 ease-out"
        :style="{ width: `${(timeRemaining / currentExercice?.time_limit) * 100}%` }"
        :class="{
          'bg-green-600': timeRemaining > 60 * 5,
          'bg-yellow-600': timeRemaining < 60 * 5,
          'bg-red-600': timeRemaining < 60 * 3,
        }"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useLanguageStore } from '@/stores/language'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'
import AudioPlayer from '@/components/common/audioPlayer.vue'
import VoiceRecorder from '@/components/exercices/voiceRecorder.vue'
import LoadingState from '@/components/exercices/LoadingState.vue'
import JSON5 from 'json5'

const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
})

const store = useLanguageStore()
const { success, error: showError, warning, info } = useToast()
const router = useRouter()

// Reactive state
const isLoading = ref(true)
const error = ref(null)
const currentQuestionIndex = ref(0)
const selectedChoices = ref({})
const writtenAnswers = ref({})
const writtenAnswer = ref('')
const isRecording = ref(false)
const showExitModal = ref(false)
const showFinishModal = ref(false)
const currentAttemptIsCorrect = ref(null)
const isSubmittingAnswer = ref(false)
const writingResponses = ref([])
const recorded_audios = ref([])
const voiceRecorderRef = ref(null)

// Timer
const timeRemaining = ref(0)
const timerInterval = ref(null)

// Computed
const currentExercice = computed(() => store.currentExercice)
const questions = computed(() => store.currentQuestions)
const userAnswers = computed(() => store.userAnswers)
const totalQuestions = computed(() => questions.value.length)
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value])
const practiceType = computed(() => store.practiceType)
const ExerciceType = computed(() => store.currentExercice?.type_exam)

const hasTimeLimit = computed(() => {
  return (
    practiceType.value === 'exam' &&
    currentExercice.value?.time_limit &&
    currentExercice.value.time_limit > 0
  )
})

const stopRecordingFromParent = async () => {
  if (voiceRecorderRef.value) {
    await voiceRecorderRef.value.stopRecording()
    updateState()
  }
}

const updateState = () => {
  if (voiceRecorderRef.value) {
    // const state = voiceRecorderRef.value.getState()
    // Object.assign(recorderState, state)
  }
}

const resetRecordingFromParent = () => {
  if (voiceRecorderRef.value) {
    voiceRecorderRef.value.resetRecording()
    updateState()
  }
}

const progress = computed(() => {
  if (totalQuestions.value === 0) return 0
  return (userAnswers.value.length / totalQuestions.value) * 100
})

// Methods
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

const getQuestionTypeLabel = (type) => {
  const labels = {
    MCQ: 'Choix multiple',
    TRUE_FALSE: 'Vrai/Faux',
    FILL_BLANK: 'Texte à trou',
    WRITTEN: 'Réponse écrite',
    SPEAKING: 'Expression orale',
  }
  return labels[type] || type
}

const getQuestionButtonClass = (index) => {
  const isAnswered = userAnswers.value.some(
    (answer) => questions.value[index] && answer.questionSlug === questions.value[index].slug,
  )
  const isCurrent = index === currentQuestionIndex.value

  if (isCurrent) {
    return 'border-primary-500 bg-primary-500 text-white'
  } else if (isAnswered) {
    return 'border-green-500 bg-green-500 text-white'
  } else {
    return 'border-gray-300 text-gray-600 hover:border-gray-400'
  }
}

const getChoiceClass = (choice) => {
  if (practiceType.value === 'exam') {
    return isChoiceSelected(choice)
      ? 'border-primary-500 bg-primary-50'
      : 'border-gray-300 hover:border-gray-400'
  } else {
    if (currentAttemptIsCorrect.value != null) {
      if (isChoiceSelected(choice) && currentAttemptIsCorrect.value)
        return 'border-green-500 bg-green-50'
      else if (isChoiceSelected(choice) && !currentAttemptIsCorrect.value)
        return 'border-red-500 bg-red-50'
      else if (!isChoiceSelected(choice) && choice?.is_correct)
        return 'border-green-500 bg-green-50'
      else return 'border-gray-300 hover:border-gray-400'
    } else {
      return isChoiceSelected(choice)
        ? 'border-primary-500 bg-primary-50'
        : 'border-gray-300 hover:border-gray-400'
    }
  }
}

const isChoiceSelected = (choice) => {
  return selectedChoices.value[currentQuestion.value.slug] === choice.slug
}

const getQuestionTitle = (question_text) => {
  try {
    const jsonObject = eval('(' + question_text + ')')
    return jsonObject?.titre || jsonObject?.sujet
  } catch (e) {
    return question_text
  }
}

const selectChoice = async (choice) => {
  isSubmittingAnswer.value = true
  if (!currentQuestion.value) return

  selectedChoices.value[currentQuestion.value.slug] = choice.slug

  // Soumettre immédiatement la réponse
  const result = await store.submitAnswer(currentQuestion.value.slug, choice.id || choice.slug)

  if (result.success) {
    // success('Réponse enregistrée')
    if (practiceType.value === 'training') {
      currentAttemptIsCorrect.value = choice?.is_correct
    }
  } else {
    showError("Erreur lors de l'enregistrement de la réponse")
  }
  isSubmittingAnswer.value = false
}

const onRecordingComplete = async (audioFile) => {
  recorded_audios.value[currentQuestionIndex.value] = audioFile
}

const get_parsed = (text) => {
  try {
    return JSON5.parse(text)
  } catch (e) {
    return text
  }
}

const saveResponse = async (response_index) => {
  //verifier si le micro enregistre toujours et couper
  if (ExerciceType.value === 'SPEAKING') {
    const state = voiceRecorderRef.value.getState()
    if (state?.isRecording) {
      await stopRecordingFromParent()
    }
  }
  if (ExerciceType.value === 'WRITING' && !writingResponses.value[response_index]) {
    warning('Merci de répondre à la question')
    return
  }
  if (ExerciceType.value === 'SPEAKING' && !recorded_audios.value[response_index]) {
    warning('Merci de répondre à la question')
    return
  }

  isSubmittingAnswer.value = true

  const slug = currentQuestion.value.slug
  const wrintingResponse = writingResponses.value[response_index]
  const exercie_type = ExerciceType.value
  const recorded_audio = recorded_audios.value[currentQuestionIndex.value]
  //ne pas attendre pour le speaking
  if (ExerciceType.value === 'SPEAKING') {
    if (currentQuestionIndex.value === totalQuestions.value - 1) {
      confirmFinish()
    } else {
      nextQuestion()
    }
  }
  // Soumettre immédiatement la réponse
  const result = await store.submitAnswer(slug, wrintingResponse, exercie_type, recorded_audio)

  if (ExerciceType.value !== 'SPEAKING') {
    if (result.success) {
      // success('Réponse enregistrée')
      if (currentQuestionIndex.value === totalQuestions.value - 1) {
        confirmFinish()
      } else {
        nextQuestion()
      }
    } else {
      showError("Erreur lors de l'enregistrement de la réponse")
    }
  }
  isSubmittingAnswer.value = false
}

const updateWrittenAnswer = () => {
  writtenAnswers.value[currentQuestion.value.slug] = writtenAnswer.value
}

const saveWrittenAnswer = async () => {
  if (!writtenAnswer.value.trim() || !currentQuestion.value) return

  // Pour les réponses écrites, on pourrait créer un choix temporaire
  // ou utiliser une logique différente selon votre backend
  success('Réponse écrite sauvegardée')
}

const toggleRecording = () => {
  isRecording.value = !isRecording.value
  if (isRecording.value) {
    info('Enregistrement démarré')
  } else {
    info('Enregistrement arrêté')
  }
}

const goToQuestion = (index) => {
  if (index >= 0 && index < totalQuestions.value) {
    currentQuestionIndex.value = index
    loadQuestionData()
  }
}

const previousQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--
    loadQuestionData()
  }
}

const nextQuestion = () => {
  currentAttemptIsCorrect.value = null
  if (currentQuestionIndex.value < totalQuestions.value - 1) {
    currentQuestionIndex.value++
    loadQuestionData()
  }
}

const loadQuestionData = () => {
  if (currentQuestion.value) {
    // Charger la réponse sauvegardée si elle existe
    const savedChoice = selectedChoices.value[currentQuestion.value.slug]
    const savedWritten = writtenAnswers.value[currentQuestion.value.slug]

    if (savedWritten) {
      writtenAnswer.value = savedWritten
    } else {
      writtenAnswer.value = ''
    }
  }
}

const startTimer = () => {
  if (!hasTimeLimit.value) return

  timeRemaining.value = currentExercice.value.time_limit // Convert to seconds

  timerInterval.value = setInterval(() => {
    timeRemaining.value--

    if (timeRemaining.value <= 0) {
      clearInterval(timerInterval.value)
      warning('Temps écoulé!')
      finishExercice()
    } else if (timeRemaining.value === 5 * 60) {
      // 5 minutes warning
      warning('Il vous reste 5 minutes!')
    } else if (timeRemaining.value === 2 * 60) {
      // 2 minute warning
      warning('Il vous reste 2 minute!')
    }
  }, 1000)
}

const confirmExit = () => {
  showExitModal.value = true
}

const exitExercice = async () => {
  const attempt_slug = store.currentAttempt.slug
  await store.completeExercice()
  clearInterval(timerInterval.value)
  store.resetCurrentExercice()
  router.push('/exercice-results/' + attempt_slug)
}

const confirmFinish = () => {
  showFinishModal.value = true
}

const finishExercice = async () => {
  try {
    clearInterval(timerInterval.value)

    // const result = await store.completeExercice()

    // if (result.success) {
    //   success(`Exercice terminé! Score: ${result.score.toFixed(1)}%`)
    await store.completeExercice()
    router.push('/exercice-results/' + store.currentAttempt?.slug || props.slug)
    // } else {
    //   showError('Erreur lors de la finalisation de l\'exercice')
    // }
  } catch (err) {
    showError('Erreur lors de la finalisation')
    console.error(err)
  }
}

const openResource = (url) => {
  window.open(url, '_blank')
}

const loadExercice = async () => {
  try {
    isLoading.value = true
    error.value = null

    // Démarrer l'exercice si pas déjà fait
    if (!store.currentExercice || store.currentExercice.slug !== props.slug) {
      const result = await store.startExercice(props.slug)

      if (!result.success) {
        throw new Error(result.error)
      }
    }

    if (questions.value.length === 0) {
      throw new Error('Aucune question trouvée pour cet exercice')
    }

    // Démarrer le timer
    startTimer()

    // Charger les données de la première question
    loadQuestionData()
  } catch (err) {
    error.value = err.message || "Erreur lors du chargement de l'exercice"
    showError(error.value)
    console.error(err)
  } finally {
    isLoading.value = false
  }
}

const retry = () => {
  loadExercice()
}

// Watchers
watch(currentQuestionIndex, () => {
  loadQuestionData()
})

// Lifecycle
onMounted(() => {
  loadExercice()
})

onUnmounted(() => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
  }
})

// Prevent accidental page refresh
onMounted(() => {
  const handleBeforeUnload = (e) => {
    if (store.currentExercice) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  window.addEventListener('beforeunload', handleBeforeUnload)

  onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  })
})
</script>

<style scoped>
.prose {
  max-width: none;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
